import { useEffect, useMemo, useState } from "react";
import { Loader2, Download, AlertTriangle, CheckCircle2, XCircle, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  listLegacySessions,
  importLegacySession,
  type LegacySession,
  type ImportProgress,
} from "@/lib/legacy-import/client";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";

type RowState = { selected: boolean; progress: ImportProgress | null };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
}

export function ImportLegacyModal({ open, onOpenChange, onImported }: Props) {
  const { user } = usePowtoonAuth();
  const [sessions, setSessions] = useState<LegacySession[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailFilter, setEmailFilter] = useState<string>("");
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [importing, setImporting] = useState(false);
  const [importedAny, setImportedAny] = useState(false);

  // Load list when opened.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setSessions(null);
    setRows({});
    setImportedAny(false);
    setEmailFilter(user?.email ?? "");
    listLegacySessions()
      .then((s) => setSessions(s))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open, user?.email]);

  const filtered = useMemo(() => {
    if (!sessions) return [];
    const q = emailFilter.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => (s.owner_email ?? "").toLowerCase().includes(q));
  }, [sessions, emailFilter]);

  const selectedIds = useMemo(
    () => Object.entries(rows).filter(([, r]) => r.selected).map(([id]) => id),
    [rows],
  );

  const toggle = (id: string, value: boolean) => {
    setRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { progress: null }), selected: value },
    }));
  };

  const toggleAll = (value: boolean) => {
    setRows((prev) => {
      const next = { ...prev };
      for (const s of filtered) {
        next[s.id] = { ...(next[s.id] ?? { progress: null }), selected: value };
      }
      return next;
    });
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((s) => rows[s.id]?.selected);

  const setProgress = (id: string, p: ImportProgress) => {
    setRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { selected: true }), progress: p },
    }));
  };

  const runImport = async () => {
    if (selectedIds.length === 0 || !sessions) return;
    setImporting(true);
    let okCount = 0;
    let errCount = 0;
    for (const id of selectedIds) {
      const s = sessions.find((x) => x.id === id);
      if (!s) continue;
      if (rows[id]?.progress?.phase === "done") continue;
      try {
        await importLegacySession(s, (p) => setProgress(id, p));
        okCount++;
        setImportedAny(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setProgress(id, { phase: "error", message: msg });
        errCount++;
      }
    }
    setImporting(false);
    if (okCount > 0) toast.success(`Imported ${okCount} scenario${okCount === 1 ? "" : "s"}`);
    if (errCount > 0) toast.error(`${errCount} import${errCount === 1 ? "" : "s"} failed`);
  };

  const handleOpenChange = (next: boolean) => {
    if (importing) return;
    onOpenChange(next);
    if (!next && importedAny) onImported?.();
  };

  const renderProgress = (p: ImportProgress | null) => {
    if (!p || p.phase === "pending") return <span className="text-xs text-muted-foreground">—</span>;
    if (p.phase === "creating")
      return (
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Creating…
        </span>
      );
    if (p.phase === "context")
      return (
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Creating context…
        </span>
      );
    if (p.phase === "thumbnail")
      return (
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Thumbnail…
        </span>
      );
    if (p.phase === "uploading")
      return (
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Docs {p.done}/{p.total}
        </span>
      );
    if (p.phase === "done")
      return (
        <span className="text-xs text-emerald-600 inline-flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Done{p.docErrors.length ? ` (${p.docErrors.length} doc err)` : ""}
        </span>
      );
    if (p.phase === "error")
      return (
        <span className="text-xs text-destructive inline-flex items-center gap-1" title={p.message}>
          <XCircle className="h-3 w-3" /> Failed
        </span>
      );
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Import from old studio</DialogTitle>
          <DialogDescription>
            Pick legacy sessions to copy into your scenarios. Each imported
            scenario gets its own new LiveAvatar context seeded from the
            original prompt and opening sentence.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex gap-2">
          <Input
            value={emailFilter}
            placeholder="Filter by owner email…"
            onChange={(e) => setEmailFilter(e.target.value)}
            disabled={loading || importing}
          />
          {emailFilter && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEmailFilter("")}
              disabled={loading || importing}
              className="gap-1 shrink-0"
            >
              <X className="h-3 w-3" /> Clear
            </Button>
          )}
        </div>

        <div className="mt-3 max-h-[55vh] overflow-y-auto rounded border border-border">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          {error && (
            <div className="p-4 text-sm text-destructive">Failed to load: {error}</div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground text-center">No sessions match.</div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <Checkbox
                      checked={allFilteredSelected}
                      onCheckedChange={(v) => toggleAll(Boolean(v))}
                      disabled={importing}
                      aria-label="Select all filtered"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Avatar</th>
                  <th className="px-3 py-2 text-left">Owner</th>
                  <th className="px-3 py-2 text-left">Docs</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const r = rows[s.id];
                  const noCtx = !s.knowledge_base_id;
                  return (
                    <tr key={s.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={!!r?.selected}
                          onCheckedChange={(v) => toggle(s.id, Boolean(v))}
                          disabled={importing}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-foreground">
                          {s.title || "(untitled)"}
                        </div>
                        <div className="flex gap-1 mt-0.5">
                          {s.mode && (
                            <Badge variant="secondary" className="text-[10px]">
                              {s.mode}
                            </Badge>
                          )}
                          {noCtx && (
                            <Badge variant="outline" className="text-[10px] gap-1 text-amber-600">
                              <AlertTriangle className="h-3 w-3" /> no context
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{s.avatar_name ?? "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{s.owner_email ?? "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {s.documents?.length ?? 0}
                      </td>
                      <td className="px-3 py-2">{renderProgress(r?.progress ?? null)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={importing}
          >
            Close
          </Button>
          <Button onClick={runImport} disabled={importing || selectedIds.length === 0} className="gap-2">
            {importing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Import {selectedIds.length || ""}{" "}
            {selectedIds.length === 1 ? "scenario" : "scenarios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}